import { Injectable, inject } from '@angular/core';
import ObjectID from 'bson-objectid';
import { DateTime } from 'luxon';
import { Observable, ReplaySubject, firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IPojoDevice } from 'src/app/model/device/IPojoDevice';
import { ShakerControlSequence } from 'src/app/model/shaker-control/shaker-control-sequence';
import { IUserData_ShakerControlProfile } from 'src/app/model/shaker-control/shaker-control-userdata-template-types';
import { ShakerFeatureDescriptor } from 'src/app/model/shaker-control/shaker-feature-descriptor';
import { IUserTemplate } from 'src/app/model/thingie/templates';
import { IUserData } from 'src/app/model/userdata';
import { isShaker } from 'src/app/utility/devices/is-shaker-predicate';
import { DeviceService } from '../device/device.service';
import { UserDataService } from '../user-data/user-data.service';
import { UserService } from '../user/user.service';

// function createRandomDevice(): IPojoDevice {
//   return {
//     _id: uuid(),
//     name: 'Device ' + Math.random()
//       .toString(36)
//       .substring(7),
//     address: '',
//     archived: false,
//     blockedBy: [],
//     configuration: {},
//     deviceInputTypes: [],
//     devType: 'unknown',
//     hidden: false,
//     info: {},
//     isManager: false,
//     owner: '',
//     packetContentDescription: {},
//     resources: {},
//     shortcuts: {},
//     teams: []
//   };
// }


@Injectable({ providedIn: 'root' })
export class ShakerProfileFeatureService {
  private devices = inject(DeviceService);

  private userDataService = inject(UserDataService);

  private userService = inject(UserService);


  public async getFeatures(): Promise<ShakerFeatureDescriptor[]> {
    return this.devices.getShakerFeatures();
  }

  public async getShakers(): Promise<IPojoDevice[]> {
    return (await firstValueFrom(this.devices.getDevices()
      .pipe(
        map(deviceList => deviceList.filter(isShaker))
      )))!;
  }


  private profileCache = new ReplaySubject<IUserData_ShakerControlProfile[]>(1);


  /**
   * Create a new shaker profile for the current user.
   *
   * @param profileName Name of the new profile
   * @param value An object containing the sequences for initial values and the profile.
   * @returns The created profile.
   * @throws `Error` if the current user could not be obtained.
   * @throws If the creation of the profile failed during communication
   *         with the backend.
   */
  public async createProfileForCurrentUser(
    profileName: string,
    value: {
      initialValues: ShakerControlSequence;
      sequence: ShakerControlSequence;
      features: string[];
    }
  ): Promise<IUserData<IUserTemplate>> {
    const currentUser = await this.userService.getCurrentUser();
    if (currentUser === undefined || currentUser._id === undefined) {
      //This will probably never happen
      throw new Error('Failed to obtain current user');
    }

    const id = new ObjectID().toHexString();
    const profileEntry: IUserTemplate = {
      _id: id,
      type: 'userSettingTemplate',
      version: 'userDefined',
      created: DateTime.now().toISO(),
      template: {
        name: profileName,
        type: 'userSettingTemplate',
        payload: {
          name: profileName,
          inputElementType: 'shaker-control',
          value,
          source: 'usersetting',
          item: {
            structureInfo: [],
            valueInfo: [
              {
                description: 'Initial values',
                unit: '',
                vartype: 'int',
                name: 'initialValues',
              }
            ],
            visibility: 'basic'
          },
        },
      },
    };

    const result = (await firstValueFrom(this.userDataService.createUserData(
      'SHAKER_PROFILE',
      currentUser._id,
      profileEntry,
      'PUBLIC'
    )))!;


    // Add to cache if successful
    (await firstValueFrom(this.profileCache
      .pipe(
        tap(cache => {
          // Add new entry to cache if not present already.
          // This is meant as a failsafe in case we add websocket pushes to the
          // user data service.
          if (cache.find(profile => profile._id === result._id) === undefined) {
            this.profileCache.next(
              [
                ...cache,
                result as IUserData_ShakerControlProfile
              ]
            );
          }
        })
      )))!;


    return result;
  }


  /**
   * @returns Observable emitting lists of {@link IUserData} objects containing the shaker control profile templates.
   */
  public getShakerControlProfiles(): Observable<IUserData_ShakerControlProfile[]> {
    const shakerData$ = this.userDataService
      .getAllUserData('SHAKER_PROFILE') as Observable<IUserData_ShakerControlProfile[]>;

    return shakerData$.pipe(
      switchMap(profiles => {
        this.profileCache.next(profiles);
        return this.profileCache;
      })
    );
  }
}
